(() => {
  const ORDER_GROUP_PROPERTY = '_mmp_order_group';
  const MCGILL_ORDER_GROUP = 'mcgill-convocation';
  const LEGACY_MCGILL_PROPERTY_NAMES = new Set([
    'convocation date',
    'convocation time',
    'mcgill convocation pickup'
  ]);
  const ADD_MESSAGE = 'McGill Convocation products must be ordered separately from regular store items. Please complete or clear your current cart before adding this item.';
  const MIXED_MESSAGE = 'McGill Convocation products must be ordered separately from regular store items. Please remove either the McGill items or the regular store items before checkout.';
  const originalFetch = window.fetch.bind(window);
  let refreshTimer;
  let cartCache = null;

  const normalizeId = (value) => String(value ?? '').trim();
  const normalizeGroup = (value) => String(value ?? '').trim().toLowerCase();
  const normalizePropertyName = (value) => String(value ?? '').trim().toLowerCase();
  const hasMcGillGroup = (properties) =>
    normalizeGroup(properties?.[ORDER_GROUP_PROPERTY]) === MCGILL_ORDER_GROUP;
  const hasLegacyMcGillProperties = (properties) =>
    Object.keys(properties || {}).some((key) =>
      LEGACY_MCGILL_PROPERTY_NAMES.has(normalizePropertyName(key))
    );

  function itemIsMcGill(item) {
    return hasMcGillGroup(item?.properties) || hasLegacyMcGillProperties(item?.properties);
  }

  function cartTypes(cart) {
    let hasMcGill = false;
    let hasRegular = false;
    (cart?.items || []).forEach((item) => {
      if (itemIsMcGill(item)) hasMcGill = true;
      else hasRegular = true;
    });
    return { hasMcGill, hasRegular, mixed: hasMcGill && hasRegular };
  }

  function incomingItemIsMcGill(item) {
    return normalizeGroup(item?.group) === MCGILL_ORDER_GROUP;
  }

  function incomingTypes(items) {
    let hasMcGill = false;
    let hasRegular = false;
    (items || []).filter((item) => item?.id).forEach((item) => {
      if (incomingItemIsMcGill(item)) hasMcGill = true;
      else hasRegular = true;
    });
    return { hasMcGill, hasRegular, mixed: hasMcGill && hasRegular };
  }

  function conflicts(cart, items) {
    if (!items.length) return false;
    const current = cartTypes(cart);
    const incoming = incomingTypes(items);
    return incoming.mixed ||
      (current.hasMcGill && incoming.hasRegular) ||
      (current.hasRegular && incoming.hasMcGill);
  }

  async function getCart() {
    const response = await originalFetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to read cart');
    return response.json();
  }

  async function currentCart() {
    if (cartCache) return cartCache;
    cartCache = await getCart();
    return cartCache;
  }

  function showToast(message) {
    let toast = document.getElementById('mcgill-cart-guard-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'mcgill-cart-guard-toast';
      toast.setAttribute('role', 'alert');
      Object.assign(toast.style, {
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: '2147483647',
        width: 'min(560px, calc(100% - 32px))',
        padding: '14px 18px',
        border: '1px solid #b42318',
        borderRadius: '8px',
        background: '#fff4f2',
        color: '#7a271a',
        fontSize: '15px',
        lineHeight: '1.4',
        boxShadow: '0 8px 30px rgba(0,0,0,.16)'
      });
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    clearTimeout(toast._mcgillTimer);
    toast._mcgillTimer = setTimeout(() => toast.remove(), 8000);
  }

  function setPending(control, pending) {
    if (!control) return;
    if (pending) {
      control.dataset.mcgillGuardPending = 'true';
      control.classList.add('loading');
      control.setAttribute('aria-disabled', 'true');
    } else {
      delete control.dataset.mcgillGuardPending;
      control.classList.remove('loading');
      control.removeAttribute('aria-disabled');
    }
  }

  function itemsFromFormData(formData) {
    const single = { id: '', group: '' };
    const multi = new Map();

    for (const [key, value] of formData.entries()) {
      if (key === 'id') {
        single.id = normalizeId(value);
        continue;
      }

      if (key === `properties[${ORDER_GROUP_PROPERTY}]`) {
        single.group = normalizeGroup(value);
        continue;
      }

      let match = key.match(/^items\[(\d+)\]\[id\]$/);
      if (match) {
        const item = multi.get(match[1]) || { id: '', group: '' };
        item.id = normalizeId(value);
        multi.set(match[1], item);
        continue;
      }

      match = key.match(/^items\[(\d+)\]\[properties\]\[_mmp_order_group\]$/);
      if (match) {
        const item = multi.get(match[1]) || { id: '', group: '' };
        item.group = normalizeGroup(value);
        multi.set(match[1], item);
      }
    }

    const items = [...multi.values()].filter((item) => item.id);
    if (single.id) items.push(single);
    return items;
  }

  function itemFromObject(value) {
    if (!value || typeof value !== 'object' || value.id == null) return null;
    return {
      id: normalizeId(value.id),
      group: normalizeGroup(value.properties?.[ORDER_GROUP_PROPERTY])
    };
  }

  function itemsFromObject(value) {
    if (!value || typeof value !== 'object') return [];
    const items = [];
    const single = itemFromObject(value);
    if (single) items.push(single);
    if (Array.isArray(value.items)) {
      value.items.forEach((entry) => {
        const item = itemFromObject(entry);
        if (item) items.push(item);
      });
    }
    return items;
  }

  function itemsFromBody(body) {
    if (!body) return [];
    if (body instanceof FormData) return itemsFromFormData(body);
    if (body instanceof URLSearchParams) {
      const data = new FormData();
      body.forEach((value, key) => data.append(key, value));
      return itemsFromFormData(data);
    }
    if (typeof body === 'string') {
      try {
        return itemsFromObject(JSON.parse(body));
      } catch (_) {
        try {
          const params = new URLSearchParams(body);
          const data = new FormData();
          params.forEach((value, key) => data.append(key, value));
          return itemsFromFormData(data);
        } catch (_) {
          return [];
        }
      }
    }
    return itemsFromObject(body);
  }

  async function itemsFromFetch(input, init) {
    if (init?.body) return itemsFromBody(init.body);
    if (input instanceof Request) {
      try {
        const text = await input.clone().text();
        return itemsFromBody(text);
      } catch (_) {
        return [];
      }
    }
    return [];
  }

  function endpointMatches(input, action) {
    try {
      const raw = input instanceof Request ? input.url : input;
      const pathname = new URL(raw, window.location.origin).pathname;
      return pathname.includes(`/cart/${action}`);
    } catch (_) {
      return false;
    }
  }

  function isCartContext(element) {
    if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) return true;
    return Boolean(element.closest('.mini-cart, cart-drawer, [id="MiniCart"], [id*="mini-cart" i], form#cart'));
  }

  async function refreshCheckoutGuard() {
    let cart;
    try {
      cart = await getCart();
      cartCache = cart;
    } catch (_) {
      return;
    }

    const { mixed } = cartTypes(cart);
    const checkoutButtons = [...document.querySelectorAll('button[name="checkout"], input[name="checkout"]')]
      .filter(isCartContext);

    document.querySelectorAll('#mcgill-mixed-cart-alert').forEach((alert) => {
      if (!mixed) alert.remove();
    });

    checkoutButtons.forEach((button) => {
      if (mixed) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.dataset.mcgillGuardDisabled = 'true';
      } else if (button.dataset.mcgillGuardDisabled === 'true') {
        button.disabled = cart.item_count === 0;
        button.removeAttribute('aria-disabled');
        delete button.dataset.mcgillGuardDisabled;
      }
    });

    document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons').forEach((container) => {
      if (!isCartContext(container)) return;
      if (mixed) {
        if (!container.dataset.mcgillGuardDisplay) container.dataset.mcgillGuardDisplay = container.style.display || '';
        container.style.display = 'none';
        container.dataset.mcgillGuardHidden = 'true';
      } else if (container.dataset.mcgillGuardHidden === 'true') {
        container.style.display = container.dataset.mcgillGuardDisplay || '';
        delete container.dataset.mcgillGuardHidden;
        delete container.dataset.mcgillGuardDisplay;
      }
    });

    if (mixed && checkoutButtons.length) {
      const firstButton = checkoutButtons[0];
      const existing = document.getElementById('mcgill-mixed-cart-alert');
      if (!existing) {
        const alert = document.createElement('div');
        alert.id = 'mcgill-mixed-cart-alert';
        alert.setAttribute('role', 'alert');
        alert.textContent = MIXED_MESSAGE;
        Object.assign(alert.style, {
          margin: '0 0 14px',
          padding: '12px 14px',
          border: '1px solid #b42318',
          borderRadius: '6px',
          background: '#fff4f2',
          color: '#7a271a',
          fontSize: '14px',
          lineHeight: '1.4'
        });
        const target = firstButton.closest('.button-container, .cart__ctas, .cart__footer') || firstButton.parentElement;
        if (target) target.insertBefore(alert, target.firstChild);
        else firstButton.before(alert);
      }
    }
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCheckoutGuard, 150);
    setTimeout(refreshCheckoutGuard, 650);
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    let action;
    try {
      action = new URL(form.action || window.location.href, window.location.origin).pathname;
    } catch (_) {
      return;
    }
    if (!action.includes('/cart/add')) return;

    if (form.dataset.mcgillGuardApproved === 'true') {
      delete form.dataset.mcgillGuardApproved;
      return;
    }

    if (form.dataset.mcgillGuardPending === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const items = itemsFromFormData(new FormData(form));
    if (!items.length) return;

    if (cartCache) {
      if (conflicts(cartCache, items)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showToast(ADD_MESSAGE);
      }
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    const submitButton = event.submitter || form.querySelector('[type="submit"]');
    form.dataset.mcgillGuardPending = 'true';
    setPending(submitButton, true);

    try {
      const cart = await currentCart();
      if (conflicts(cart, items)) {
        showToast(ADD_MESSAGE);
        return;
      }
      form.dataset.mcgillGuardApproved = 'true';
    } catch (_) {
      form.dataset.mcgillGuardApproved = 'true';
    } finally {
      delete form.dataset.mcgillGuardPending;
      setPending(submitButton, false);
    }

    if (form.dataset.mcgillGuardApproved === 'true') {
      form.requestSubmit(event.submitter || undefined);
    }
  }, true);

  document.addEventListener('click', async (event) => {
    const addControl = event.target.closest?.('add-to-cart[data-variant-id]');
    if (!addControl) return;

    if (addControl.dataset.mcgillGuardApproved === 'true') {
      delete addControl.dataset.mcgillGuardApproved;
      return;
    }

    if (addControl.dataset.mcgillGuardPending === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const id = normalizeId(addControl.dataset.variantId);
    if (!id) return;
    const items = [{ id, group: normalizeGroup(addControl.dataset.orderGroup) }];

    if (cartCache) {
      if (conflicts(cartCache, items)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showToast(ADD_MESSAGE);
      }
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    addControl.dataset.mcgillGuardPending = 'true';
    setPending(addControl, true);

    try {
      const cart = await currentCart();
      if (conflicts(cart, items)) {
        showToast(ADD_MESSAGE);
        return;
      }
      addControl.dataset.mcgillGuardApproved = 'true';
    } catch (_) {
      addControl.dataset.mcgillGuardApproved = 'true';
    } finally {
      delete addControl.dataset.mcgillGuardPending;
      setPending(addControl, false);
    }

    if (addControl.dataset.mcgillGuardApproved === 'true') {
      addControl.click();
    }
  }, true);

  window.fetch = async function(input, init) {
    if (endpointMatches(input, 'add')) {
      const items = await itemsFromFetch(input, init);
      if (items.length) {
        try {
          const cart = await currentCart();
          if (conflicts(cart, items)) {
            showToast(ADD_MESSAGE);
            return new Response(JSON.stringify({
              status: 422,
              message: 'Cart Error',
              description: ADD_MESSAGE
            }), {
              status: 422,
              headers: { 'Content-Type': 'application/json' }
            });
          }
        } catch (_) {
          // If the cart can't be read, leave Shopify's normal add-to-cart flow untouched.
        }
      }
    }

    const response = await originalFetch(input, init);
    if (endpointMatches(input, 'add') || endpointMatches(input, 'change') || endpointMatches(input, 'update') || endpointMatches(input, 'clear')) {
      cartCache = null;
      scheduleRefresh();
    }
    return response;
  };

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest?.('button[name="checkout"], input[name="checkout"]');
    if (checkout?.dataset.mcgillGuardDisabled === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      showToast(MIXED_MESSAGE);
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshCheckoutGuard, { once: true });
  } else {
    refreshCheckoutGuard();
  }

  const observer = new MutationObserver(() => scheduleRefresh());
  const observe = () => {
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.body) observe();
  else document.addEventListener('DOMContentLoaded', observe, { once: true });
})();